import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Alignment,
  List,
  Indent,
  Link,
  BlockQuote,
  Code,
  CodeBlock,
  Highlight,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageUpload,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  MediaEmbed,
  Autosave,
  Clipboard,
  DragDrop,
  Typing,
  SourceEditing,
  GeneralHtmlSupport,
  HtmlEmbed,
  WordCount,
  Markdown,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { toast } from 'react-toastify';
import { marked } from 'marked';
import { addPracticeQuestion, updatePracticeQuestion } from '../../../services/adminServices/MakerPracticeServices';

const LICENSE_KEY = 'GPL';

export default function PreviewEditor({
  openModal,
  setOpenModal,
  handleCloseModal,
  initialContent,
  rowData,
  setEditorOpen,
  fetchLevelQuestions,
}) {
  const editorContainerRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [editorMarkdownContent, setEditorMarkdownContent] = useState('');
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  useEffect(() => {
    if (initialContent && initialContent.practiceID) {
      const htmlContent = marked(initialContent.practice_md_path || '');
      setEditorMarkdownContent(htmlContent);
      setIsUpdateMode(true);
    } else {
      setEditorMarkdownContent('');
      setIsUpdateMode(false);
    }
  }, [initialContent]);

  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  const editorConfig = useMemo(() => {
    if (!isLayoutReady) return null;
    return {
      toolbar: {
        items: [
          'sourceEditing',
          'heading',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'fontSize',
          'fontFamily',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'link',
          'bulletedList',
          'numberedList',
          'alignment',
          '|',
          'highlight',
          'blockQuote',
          'code',
          'codeBlock',
          'htmlEmbed',
          'mediaEmbed',
          'imageUpload',
          'insertTable',
          '|',
          'undo',
          'redo',
        ],
        shouldNotGroupWhenFull: true,
      },
      plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        Alignment,
        List,
        Indent,
        Link,
        BlockQuote,
        Code,
        CodeBlock,
        Highlight,
        Image,
        ImageToolbar,
        ImageCaption,
        ImageUpload,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        MediaEmbed,
        Autosave,
        Clipboard,
        DragDrop,
        Typing,
        SourceEditing,
        GeneralHtmlSupport,
        HtmlEmbed,
        WordCount,
        Markdown,
      ],
      htmlSupport: {
        allow: [
          {
            name: /.*/,
            attributes: true,
            classes: true,
            styles: true,
          },
        ],
      },
      image: {
        toolbar: [
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
        ],
      },
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableCellProperties',
          'tableProperties',
        ],
      },
      licenseKey: LICENSE_KEY,
      placeholder: 'Type your Question here!',
    };
  }, [isLayoutReady]);

  const handleSubmit = async () => {
    try {
      const obj = {
        
        practice_type: rowData.level.toLowerCase(),
        practice_md_path: editorMarkdownContent,
        // practice_html_path: '',
      };

      if (isUpdateMode) {
       const response= await updatePracticeQuestion(
          initialContent.practiceID,
          rowData.moduleId,
          rowData.topicId,
          obj
        );
        toast.success(response?.data.message);
      } else {
       const response =await addPracticeQuestion(rowData.moduleId, rowData.topicId, obj);
        toast.success(response?.data.message);
      }

      setEditorMarkdownContent('');
      setEditorOpen(false);
      setOpenModal(false);
      fetchLevelQuestions();
    } catch (error) {
      toast.error( error?.response?.data.message||error?.message ||error?.error);
    }
  };

  return (
    <Dialog open={openModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
      <DialogContent>
        <div className="editor-container" ref={editorContainerRef}>
          {isLayoutReady && editorConfig && (
            <CKEditor
              editor={ClassicEditor}
              config={editorConfig}
              data={editorMarkdownContent}
              onChange={(event, editor) => {
                const markdownData = editor.getData();
                setEditorMarkdownContent(markdownData);
              }}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
